import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Form, FormItem, Input, Modal, Alert, toast } from '@crosscode/gravity-components'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import { useAuth } from '@/app/AuthProvider'

type AuthMethod = 'call' | 'telegram'

const TG_BOT_URL = 'https://t.me/globus_auth_bot'
const PHONE_RE = /^\+7\d{10}$/

function normalizePhone(raw: string) {
  let value = raw.replace(/\D/g, '')
  if (!value.startsWith('7')) value = '7' + value
  value = '+' + value
  return value.slice(0, 12)
}

function formatCallPhone(raw: string) {
  const digits = raw.replace(/\D/g, '')
  if (digits.length === 11) {
    return digits.replace(/(\d)(\d{3})(\d{3})(\d{2})(\d{2})/, '$1 ($2) $3-$4-$5')
  }
  return raw
}

export function LoginPage() {
  const { user, loading, refresh } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: string } | null)?.from || '/'

  const [phone, setPhone] = useState('+7')
  const [method, setMethod] = useState<AuthMethod>('call')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [registerOpen, setRegisterOpen] = useState(false)
  const [regName, setRegName] = useState('')
  const [regPosition, setRegPosition] = useState('')
  const [regPhone, setRegPhone] = useState('+7')

  const [callOpen, setCallOpen] = useState(false)
  const [callNumber, setCallNumber] = useState('')
  const [checkId, setCheckId] = useState<string | null>(null)

  const [tgOpen, setTgOpen] = useState(false)
  const [tgCode, setTgCode] = useState('')
  const [tgError, setTgError] = useState<string | null>(null)
  const [tgLocked, setTgLocked] = useState(false)
  const tgVerifying = useRef(false)

  const openTelegram = (hint?: string) => {
    setTgCode('')
    setTgError(null)
    setTgLocked(false)
    setMethod('telegram')
    setTgOpen(true)
    if (hint) setMessage(hint)
  }

  const openRegister = (prefillPhone = phone) => {
    setRegPhone(normalizePhone(prefillPhone))
    setRegisterOpen(true)
  }

  useEffect(() => {
    if (!checkId || !callOpen) return
    const timer = setInterval(async () => {
      try {
        const data = await api<{ status?: string }>('/api/auth/status', {
          method: 'POST',
          body: { check_id: checkId },
        })
        if (data.status === 'confirmed') {
          clearInterval(timer)
          setCallOpen(false)
          await refresh()
          toast.success('Вход выполнен')
          navigate(from, { replace: true })
        } else if (data.status === 'expired') {
          clearInterval(timer)
          setCallOpen(false)
          setCheckId(null)
          setError('Время для звонка истекло. Попробуйте снова.')
          setMessage(null)
        }
      } catch {
        /* keep polling */
      }
    }, 5000)
    return () => clearInterval(timer)
  }, [checkId, callOpen, refresh, navigate, from])

  const canSubmit = useMemo(() => PHONE_RE.test(phone), [phone])
  const canRegister = useMemo(
    () => Boolean(regName.trim() && regPosition.trim() && PHONE_RE.test(regPhone)),
    [regName, regPosition, regPhone],
  )

  if (!loading && user) {
    return <Navigate to={from} replace />
  }

  const startAuth = async () => {
    setError(null)
    setMessage(null)

    if (!PHONE_RE.test(phone)) {
      setError('Введите корректный номер (+7XXXXXXXXXX)')
      return
    }

    setBusy(true)
    try {
      if (method === 'telegram') {
        openTelegram('Введите код из Telegram-бота')
        return
      }

      const data = await api<{
        check_id?: string
        call_phone?: string
        use_telegram_backup?: boolean
        message?: string
      }>('/api/auth/start', { method: 'POST', body: { phone } })

      if (data.use_telegram_backup) {
        openTelegram('Звонок недоступен — войдите через Telegram')
        return
      }

      setCheckId(data.check_id || null)
      setCallNumber(data.call_phone ? formatCallPhone(data.call_phone) : '')
      setCallOpen(true)
      setMessage(data.message || 'Ожидаем подтверждение звонка…')
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        openRegister(phone)
        setError('Номер не зарегистрирован. Отправьте заявку.')
      } else {
        setError(err instanceof Error ? err.message : 'Ошибка авторизации')
      }
    } finally {
      setBusy(false)
    }
  }

  const register = async () => {
    if (!canRegister) {
      setError('Заполните все поля корректно')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api('/api/register', {
        method: 'POST',
        body: { name: regName.trim(), position: regPosition.trim(), phone: regPhone },
      })
      toast.success('Заявка отправлена')
      setRegisterOpen(false)
      setPhone(regPhone)
      setMessage('Заявка отправлена администратору. Дождитесь одобрения и войдите снова.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка регистрации')
    } finally {
      setBusy(false)
    }
  }

  const verifyTg = async (code = tgCode) => {
    if (tgVerifying.current || tgLocked || !/^\d{5}$/.test(code)) return
    tgVerifying.current = true
    setBusy(true)
    setTgError(null)
    setError(null)
    try {
      const data = await api<{ status?: string }>('/api/auth/verify-tg-code', {
        method: 'POST',
        body: { phone, code },
      })
      if (data.status === 'confirmed') {
        setTgOpen(false)
        await refresh()
        toast.success('Вход выполнен')
        navigate(from, { replace: true })
      } else {
        setTgError('Код не подтверждён')
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setTgLocked(true)
        setTgError(err.message || 'Достигнут лимит попыток')
      } else if (err instanceof ApiError && err.status === 403) {
        setTgOpen(false)
        openRegister(phone)
        setError('Номер не зарегистрирован. Отправьте заявку.')
      } else {
        setTgError(err instanceof Error ? err.message : 'Ошибка проверки кода')
      }
    } finally {
      setBusy(false)
      tgVerifying.current = false
    }
  }

  const onTgCodeChange = (value: string) => {
    const next = value.replace(/\D/g, '').slice(0, 5)
    setTgCode(next)
    if (next.length === 5 && !tgLocked) {
      void verifyTg(next)
    }
  }

  const selectMethod = (next: AuthMethod) => {
    setMethod(next)
    setError(null)
    setMessage(null)
  }

  return (
    <div className="kit-auth">
      <aside className="kit-auth__aside">
        <div className="kit-auth__aside-inner">
          <p className="kit-auth__eyebrow">Оборудование магазинов</p>
          <h1 className="kit-auth__logo">
            <span className="kit-auth__logo-line">
              <span className="kit-auth__logo-text">KIT</span>
            </span>
            <span className="kit-auth__logo-line kit-auth__logo-line--muted">
              <span className="kit-auth__logo-text">Support</span>
            </span>
          </h1>
        </div>
      </aside>

      <main className="kit-auth__main">
        <div className="kit-auth__panel">
          <header className="kit-auth__header">
            <h2 className="kit-auth__heading">Вход</h2>
            <p className="kit-auth__sub">По номеру телефона · звонок или Telegram</p>
          </header>

          <Form layout="vertical" onFinish={startAuth} requiredMark={false} className="kit-auth__form">
            <FormItem label="Номер телефона" required>
              <Input
                value={phone}
                onChange={(e) => setPhone(normalizePhone(e.target.value))}
                placeholder="+79001234567"
                size="large"
                className="kit-auth__phone"
              />
            </FormItem>

            <div className="kit-auth__method" role="radiogroup" aria-label="Способ авторизации">
              <button
                type="button"
                role="radio"
                aria-checked={method === 'call'}
                className={`kit-auth__tab${method === 'call' ? ' is-active' : ''}`}
                onClick={() => selectMethod('call')}
              >
                Звонок
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={method === 'telegram'}
                className={`kit-auth__tab${method === 'telegram' ? ' is-active' : ''}`}
                onClick={() => selectMethod('telegram')}
              >
                Telegram
              </button>
            </div>

            {error ? <Alert type="error" message={error} showIcon className="kit-auth__alert" /> : null}
            {message ? <Alert type="info" message={message} showIcon className="kit-auth__alert" /> : null}

            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              loading={busy}
              disabled={!canSubmit}
              className="kit-auth__submit"
            >
              {method === 'telegram' ? 'Продолжить в Telegram' : 'Продолжить'}
            </Button>
          </Form>

          <div className="kit-auth__footer">
            <button type="button" className="kit-auth__link" onClick={() => openRegister()}>
              Зарегистрироваться
            </button>
            <span className="kit-auth__dot" aria-hidden />
            <Link to="/preview" className="kit-auth__link kit-auth__link--muted">
              О системе
            </Link>
          </div>
        </div>
      </main>

      <Modal
        title="Регистрация"
        open={registerOpen}
        onCancel={() => setRegisterOpen(false)}
        onOk={register}
        confirmLoading={busy}
        okText="Отправить"
        okButtonProps={{ disabled: !canRegister }}
      >
        <Form layout="vertical">
          <FormItem label="ФИО" required>
            <Input
              value={regName}
              onChange={(e) => setRegName(e.target.value)}
              placeholder="Иванов Иван Иванович"
            />
          </FormItem>
          <FormItem label="Должность" required>
            <Input
              value={regPosition}
              onChange={(e) => setRegPosition(e.target.value)}
              placeholder="Кассир"
            />
          </FormItem>
          <FormItem label="Номер телефона" required>
            <Input
              value={regPhone}
              onChange={(e) => setRegPhone(normalizePhone(e.target.value))}
              placeholder="+79001234567"
            />
          </FormItem>
        </Form>
      </Modal>

      <Modal
        title="Подтверждение звонком"
        open={callOpen}
        onCancel={() => {
          setCallOpen(false)
          setCheckId(null)
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setCallOpen(false)
              setCheckId(null)
            }}
          >
            Закрыть
          </Button>,
        ]}
      >
        <div className="kit-auth__call">
          <p className="kit-auth__call-label">Позвоните на номер</p>
          <p className="kit-auth__call-number">{callNumber || '—'}</p>
          <p className="kit-auth__call-hint">После звонка вход произойдёт автоматически.</p>
        </div>
      </Modal>

      <Modal
        title="Авторизация через Telegram"
        open={tgOpen}
        onCancel={() => setTgOpen(false)}
        onOk={() => verifyTg()}
        confirmLoading={busy}
        okText="Подтвердить"
        okButtonProps={{ disabled: tgLocked || tgCode.length !== 5 }}
      >
        <p>
          Перейдите в бота{' '}
          <a href={TG_BOT_URL} target="_blank" rel="noreferrer">
            @globus_auth_bot
          </a>{' '}
          и введите 5-значный код.
        </p>
        <Input
          value={tgCode}
          onChange={(e) => onTgCodeChange(e.target.value)}
          placeholder="12345"
          maxLength={5}
          size="large"
          disabled={tgLocked}
          autoFocus
        />
        {tgError ? (
          <Alert type="error" message={tgError} showIcon style={{ marginTop: 12 }} />
        ) : null}
        {tgLocked ? (
          <p style={{ marginTop: 8, color: 'var(--text-secondary)', fontSize: 13 }}>
            Можно войти по звонку: закройте окно и выберите «Звонок».
          </p>
        ) : null}
      </Modal>
    </div>
  )
}
